import { Button, type ButtonProps } from "@timeghost/ui/shadcn/button";
import { Check, Copy } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const COPIED_MS = 1500;
const ICON_TRANSITION = { duration: 0.2, ease: "easeOut" } as const;

interface CopyButtonProps extends ButtonProps {
	readonly copiedMs?: number;
	/** Replaces the copy glyph shown while idle. */
	readonly idleIcon?: ReactNode;
	readonly value: string;
}

interface CopyStatusIconProps {
	readonly copied: boolean;
	readonly idleIcon?: ReactNode;
}

function CopyStatusIcon({ copied, idleIcon }: CopyStatusIconProps): JSX.Element {
	return (
		<span aria-hidden className="relative size-[1em] shrink-0" data-icon="inline-end">
			<AnimatePresence initial={false} mode="wait">
				<motion.span
					animate={{ opacity: 1, scale: 1 }}
					className="absolute inset-0 flex items-center justify-center"
					exit={{ opacity: 0, scale: 0.95 }}
					initial={{ opacity: 0, scale: 0.95 }}
					key={copied ? "check" : "copy"}
					transition={ICON_TRANSITION}>
					{copied ? <Check /> : (idleIcon ?? <Copy />)}
				</motion.span>
			</AnimatePresence>
		</span>
	);
}

export default function CopyButton({
	value,
	copiedMs = COPIED_MS,
	children,
	disabled,
	idleIcon,
	onClick,
	trailingIcon,
	type = "button",
	variant = "outline",
	...props
}: CopyButtonProps): JSX.Element {
	const { t } = useTranslation();
	const copiedTimerRef = useRef(0);
	const [copied, setCopied] = useState(false);
	const [seenValue, setSeenValue] = useState(value);
	if (seenValue !== value) {
		setSeenValue(value);
		setCopied(false);
	}

	useEffect(
		() => () => {
			window.clearTimeout(copiedTimerRef.current);
		},
		[]
	);

	return (
		<Button
			aria-label={children ? undefined : t("shell.copy.label")}
			disabled={disabled || value.length === 0}
			onClick={(event) => {
				onClick?.(event);
				if (event.defaultPrevented || value.length === 0 || !navigator.clipboard) {
					return;
				}
				void navigator.clipboard.writeText(value).then(
					() => {
						window.clearTimeout(copiedTimerRef.current);
						setCopied(true);
						copiedTimerRef.current = window.setTimeout(() => {
							setCopied(false);
						}, copiedMs);
					},
					() => {
						setCopied(false);
					}
				);
			}}
			trailingIcon={trailingIcon ?? <CopyStatusIcon copied={copied} idleIcon={idleIcon} />}
			type={type}
			variant={variant}
			{...props}>
			{children}
			<span aria-live="polite" className="sr-only">
				{copied ? t("shell.copy.copied") : ""}
			</span>
		</Button>
	);
}
